'use client';

import { useCallback, useMemo, useEffect, useRef } from 'react';
import { createLogger } from '@/shared/lib/logger';
import { useJobQueueStore, type Job } from '@/features/design/job-queue';
import {
  useCompletedRendersForAngles,
  useActiveUpscalesForRenders,
} from '@/features/design/job-queue';
import { RenderStatus } from '@/shared/constants';
import { isRenderInQueue } from '@/shared/lib/render-status';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import type { ConceptAngle } from '@/types/concept';
import type { Render } from '@/types/render';
import { isUserVisibleRenderType } from '@/types/render';
import {
  useRendersForConcept,
  filterCompletedRendersWithFilesByAngle,
} from '@/features/design/render/model/renders-cache';

const logger = createLogger('useDerivativeAngleData');

interface UseDerivativeAngleDataProps {
  conceptId: string;
  mainAngleId: string | undefined;
  derivativeAngles: ConceptAngle[];
}

export interface AngleRenderData {
  angleRenders: Render[];
  completedRenders: Render[];
  activeRender: Job | undefined;
  completedRenderFromStore: ReturnType<
    typeof useCompletedRendersForAngles
  > extends Map<string, infer V>
    ? V | undefined
    : never;
  activeUpscale: Job | undefined;
  hasNewRenderFromStore: boolean;
  allRenders: Array<{
    id: string;
    url: string;
    isFromStore: boolean;
    render: Render | undefined;
  }>;
  totalRenders: number;
  currentRenderItem:
    | {
        id: string;
        url: string;
        isFromStore: boolean;
        render: Render | undefined;
      }
    | undefined;
  currentRender: Render | undefined;
  currentIdx: number;
}

export function useDerivativeAngleData({
  conceptId,
  mainAngleId,
  derivativeAngles,
}: UseDerivativeAngleDataProps) {
  const ui = useConceptStore((state) => state.ui);
  const setSelectedRenderIdRaw = useConceptStore(
    (state) => state.setSelectedRenderId
  );
  // Carousel callsites pass `(conceptAngleId, renderId)` — close the
  // store's `(…, conceptId)` arg over the route conceptId here so callers
  // don't have to thread it themselves. The conceptId is needed to drive
  // the per-angle debounced API sync inside the store.
  const setSelectedRenderId = useCallback(
    (conceptAngleId: string, renderId: string) => {
      setSelectedRenderIdRaw(conceptAngleId, renderId, conceptId);
    },
    [setSelectedRenderIdRaw, conceptId]
  );

  // Track previous render counts to detect new renders
  const previousRenderCounts = useRef<Record<string, number>>({});
  // Track if we've done initial load from API to avoid overwriting saved selection
  const hasLoadedFromApi = useRef<Record<string, boolean>>({});

  // Renders are owned by TanStack Query.
  const rendersForConcept = useRendersForConcept(conceptId);

  const hasMainRender = useMemo(
    () =>
      filterCompletedRendersWithFilesByAngle(rendersForConcept, mainAngleId)
        .length > 0,
    [rendersForConcept, mainAngleId]
  );

  // Get job queue for filtering active renders
  const jobQueue = useJobQueueStore((state) => state.jobQueue);

  // Pre-computed index: group renders by angle ID once, then do O(1) lookups
  const rendersByAngle = useMemo(() => {
    const map = new Map<string, Render[]>();
    rendersForConcept.forEach((render) => {
      const angleId = render.relationships?.concept_angle?.data?.id;
      if (angleId) {
        const list = map.get(angleId) || [];
        list.push(render);
        map.set(angleId, list);
      }
    });
    // Sort each list once
    map.forEach((list) =>
      list.sort(
        (a, b) =>
          new Date(a.attributes.created_at).getTime() -
          new Date(b.attributes.created_at).getTime()
      )
    );
    return map;
  }, [rendersForConcept]);

  // O(1) lookup instead of O(n) scan per call
  const getRendersByAngleId = useCallback(
    (angleId: string): Render[] => rendersByAngle.get(angleId) || [],
    [rendersByAngle]
  );

  // Get all completed renders from store for all derivative angles
  const derivativeAngleIds = useMemo(
    () => derivativeAngles.map((a) => a.id),
    [derivativeAngles]
  );
  const completedRendersFromStore =
    useCompletedRendersForAngles(derivativeAngleIds);

  // Collect active render jobs (exclude upscales)
  const activeRenders = useMemo(() => {
    const map = new Map<string, Job>();
    derivativeAngles.forEach((angle) => {
      const job = jobQueue.find(
        (j) =>
          j.conceptAngleId === angle.id &&
          j.jobType === 'render' &&
          isRenderInQueue(j.status)
      );
      if (job) map.set(angle.id, job);
    });
    return map;
  }, [derivativeAngles, jobQueue]);

  // Collect all render IDs for batch upscale check
  const allRenderIds = useMemo(() => {
    return derivativeAngles.flatMap((angle) => {
      const angleRenders = getRendersByAngleId(angle.id);
      const completedRenders = angleRenders.filter(
        (r) =>
          r.attributes.status === RenderStatus.COMPLETED &&
          r.relationships?.file?.meta?.url &&
          isUserVisibleRenderType(r.attributes.render_type)
      );
      return completedRenders.map((r) => r.id);
    });
  }, [derivativeAngles, getRendersByAngleId]);

  // Get all active upscales for all renders
  const activeUpscalesMap = useActiveUpscalesForRenders(allRenderIds);

  // Get data for a specific angle
  const getAngleRenderData = useCallback(
    (angle: ConceptAngle): AngleRenderData => {
      const angleRenders = getRendersByAngleId(angle.id);
      const completedRenders = angleRenders.filter(
        (r) =>
          r.attributes.status === RenderStatus.COMPLETED &&
          r.relationships?.file?.meta?.url &&
          isUserVisibleRenderType(r.attributes.render_type)
      );

      const activeRender = activeRenders.get(angle.id);
      const completedRenderFromStore = completedRendersFromStore.get(angle.id);

      // Check for new render from Store
      const hasNewRenderFromStore =
        completedRenderFromStore?.jobType === 'render' &&
        !!completedRenderFromStore.imageUrl &&
        !completedRenders.some(
          (r) =>
            r.relationships?.file?.meta?.url ===
            completedRenderFromStore.imageUrl
        );

      // Create temporary render object for new render from Store
      const newRenderFromStore = hasNewRenderFromStore
        ? {
            id: `temp-${completedRenderFromStore.id}`,
            url: completedRenderFromStore.imageUrl!,
            isFromStore: true,
            render: undefined,
          }
        : null;

      // Create unified array of all renders (API + Store)
      const allRenders = [
        ...completedRenders.map((r) => ({
          id: r.id,
          url: r.relationships?.file?.meta?.url || '',
          isFromStore: false,
          render: r,
        })),
        ...(newRenderFromStore ? [newRenderFromStore] : []),
      ];

      const totalRenders = allRenders.length;

      // Resolve selection by ID. The live store value wins; before the store is
      // seeded (loadSelectedRenderIds runs in the PARENT ConceptEditor effect,
      // which fires AFTER this child hook's effect), fall back to the angle's
      // server-saved selected_render_id so the first frame paints the saved
      // render instead of newest — no "tail" flash on concept switch. Newest is
      // the last resort only when neither memory nor server has a selection.
      const selectedRenderId =
        ui.selectedRenderId[angle.id] ?? angle.attributes.selected_render_id;
      const currentRenderItem = selectedRenderId
        ? allRenders.find((r) => r.id === selectedRenderId) ||
          allRenders[allRenders.length - 1]
        : allRenders[allRenders.length - 1];

      const currentRender = currentRenderItem?.render;

      // Current index
      const currentIdx = currentRenderItem
        ? allRenders.findIndex((r) => r.id === currentRenderItem.id)
        : totalRenders > 0
          ? totalRenders - 1
          : 0;

      // Active upscale
      const activeUpscale = currentRender?.id
        ? activeUpscalesMap.get(currentRender.id)
        : undefined;

      return {
        angleRenders,
        completedRenders,
        activeRender,
        completedRenderFromStore,
        activeUpscale,
        hasNewRenderFromStore,
        allRenders,
        totalRenders,
        currentRenderItem,
        currentRender,
        currentIdx,
      };
    },
    [
      getRendersByAngleId,
      activeRenders,
      completedRendersFromStore,
      activeUpscalesMap,
      ui.selectedRenderId,
    ]
  );

  // Auto-scroll to last render when new render completes
  useEffect(() => {
    derivativeAngles.forEach((angle) => {
      const angleRenders = getRendersByAngleId(angle.id);
      const completedRenders = angleRenders.filter(
        (r) =>
          r.attributes.status === RenderStatus.COMPLETED &&
          r.relationships?.file?.meta?.url &&
          isUserVisibleRenderType(r.attributes.render_type)
      );

      const completedRenderFromStore = completedRendersFromStore.get(angle.id);
      const hasNewRenderFromStore =
        completedRenderFromStore?.jobType === 'render' &&
        !!completedRenderFromStore.imageUrl &&
        !completedRenders.some(
          (r) =>
            r.relationships?.file?.meta?.url ===
            completedRenderFromStore.imageUrl
        );
      const totalRenders =
        completedRenders.length + (hasNewRenderFromStore ? 1 : 0);

      const previousCount = previousRenderCounts.current[angle.id] || 0;
      // The angle's server-saved selection is authoritative until the store is
      // seeded. This child hook's effect runs BEFORE the parent's
      // loadSelectedRenderIds, so reading only the store here would treat a
      // not-yet-seeded server selection as "none" and auto-jump to newest,
      // clobbering it (and PATCHing newest back). Fold the server value in so a
      // saved selection is always respected regardless of effect ordering.
      const savedSelection =
        ui.selectedRenderId[angle.id] ?? angle.attributes.selected_render_id;
      const isInitialLoad = !hasLoadedFromApi.current[angle.id];

      // Create unified array for selection
      const allRendersForSelection = [
        ...completedRenders.map((r) => ({ id: r.id, isFromStore: false })),
        ...(hasNewRenderFromStore
          ? [{ id: `temp-${completedRenderFromStore.id}`, isFromStore: true }]
          : []),
      ];

      // On initial load: respect saved selection from API
      if (isInitialLoad && totalRenders > 0) {
        hasLoadedFromApi.current[angle.id] = true;

        if (!savedSelection) {
          const lastRenderItem =
            allRendersForSelection[allRendersForSelection.length - 1];
          if (lastRenderItem) {
            setSelectedRenderId(angle.id, lastRenderItem.id);
          }
        }
      }
      // On subsequent updates: auto-select new renders
      else if (
        !isInitialLoad &&
        totalRenders > previousCount &&
        totalRenders > 0 &&
        !savedSelection
      ) {
        const lastRenderItem =
          allRendersForSelection[allRendersForSelection.length - 1];
        if (lastRenderItem) {
          setSelectedRenderId(angle.id, lastRenderItem.id);
        }
      }
      // Replace temporary ID with real ID when render appears in API
      else if (
        !isInitialLoad &&
        savedSelection?.startsWith('temp-') &&
        !hasNewRenderFromStore &&
        completedRenders.length > 0
      ) {
        const lastRender = completedRenders[completedRenders.length - 1];
        if (lastRender) {
          logger.info('Replacing temporary ID with real ID:', {
            angleId: angle.id,
            tempId: savedSelection,
            realId: lastRender.id,
          });
          setSelectedRenderId(angle.id, lastRender.id);
        }
      }

      previousRenderCounts.current[angle.id] = totalRenders;
    });
  }, [
    derivativeAngles,
    getRendersByAngleId,
    completedRendersFromStore,
    setSelectedRenderId,
    ui.selectedRenderId,
  ]);

  return {
    hasMainRender,
    getRendersByAngleId,
    getAngleRenderData,
    setSelectedRenderId,
  };
}
